import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dbb6kvc8sc1eisn2to8g";
const MAX_MESSAGE = 500;

type Status = "idle" | "submitting" | "success" | "error";

const contactChannels = [
  { icon: "ri-mail-line", label: "编辑邮箱", value: "editor@demo-18jin.example", note: "内容建议与纠错" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "ADULT_ARCHIVE", note: "演示账号" },
  { icon: "ri-telegram-line", label: "Telegram", value: "@adult_archive_demo", note: "演示账号" },
  { icon: "ri-time-line", label: "响应时间", value: "工作日 48 小时内", note: "演示说明" },
];

export default function ContactPage() {
  useDocumentMeta({
    title: "联系我们 - 18禁成人内容资讯平台",
    description:
      "联系18禁编辑部：提交内容建议、条目纠错或合作咨询。表单支持前端校验与提交反馈，请勿提交个人敏感信息。",
    keywords: "联系18禁,内容建议,条目纠错,合作咨询,成人内容平台",
  });

  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const content = String(formData.get("message") ?? "").trim();
    const subject = String(formData.get("subject") ?? "").trim();

    setFormError("");

    if (!name || !email || !content) {
      setStatus("error");
      setFormError("请完整填写姓名、邮箱与留言内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式似乎不正确，请检查后再提交。");
      return;
    }
    if (content.length > MAX_MESSAGE) {
      setStatus("error");
      setFormError(`留言内容请控制在 ${MAX_MESSAGE} 字以内。`);
      return;
    }

    if (honeypot) {
      setStatus("success");
      setMessage("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    payload.set("name", name);
    payload.set("email", email);
    payload.set("message", content);
    if (subject) payload.set("subject", subject);

    setStatus("submitting");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? "";
      const failedBySpam = String(serverMsg).toLowerCase().includes("spam");
      const succeeded = response.ok && parsed?.code === "OK" && !failedBySpam;

      if (succeeded) {
        setStatus("success");
        setMessage("留言已提交，感谢你的反馈。我们会在工作日尽快查看。");
        form.reset();
        return;
      }

      setStatus("error");
      setFormError(serverMsg || "提交失败，请稍后重试，或通过邮箱联系我们。");
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，提交没有成功。请稍后重试。");
    }
  };

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <span className="text-foreground-700">联系我们</span>
          </nav>
          <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[34px]">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm leading-[1.95] text-foreground-600">
            内容纠错、条目补充、合作咨询，或者只是想聊聊某部作品，都可以通过下面的表单告诉我们。请在留言中注明具体作品或页面。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">GET IN TOUCH</span>
            </div>
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">编辑部的联系方式</h2>
            <p className="mt-3 text-[13px] leading-[1.9] text-foreground-600">
              以下联系方式与账号均为虚构演示信息，仅用于展示页面结构。真实反馈请使用右侧表单。
            </p>

            <div className="mt-6 space-y-3">
              {contactChannels.map((channel) => (
                <div
                  key={channel.label}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${channel.icon} text-base`} aria-hidden="true"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] tracking-[0.16em] text-foreground-400">{channel.label}</p>
                    <p className="mt-1 break-words text-sm text-foreground-900">{channel.value}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">{channel.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold text-accent-900">
                <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
                提交前请注意
              </p>
              <ul className="mt-2.5 space-y-1.5 text-[11px] leading-relaxed text-accent-900/80">
                <li>· 请勿提交身份证号、住址等个人敏感信息</li>
                <li>· 本站不提供资源获取渠道，相关请求无法处理</li>
                <li>· 涉及具体作品的反馈，请附上页面标题</li>
              </ul>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <div className="mb-6">
              <h2 className="font-heading text-lg text-foreground-950 md:text-xl">在线留言</h2>
              <p className="mt-1.5 text-xs text-foreground-500">带 * 的字段为必填项</p>
            </div>

            <form
              id="contact-form-18jin"
              data-readdy-form
              onSubmit={handleSubmit}
              action={SUBMIT_ADDR}
              method="post"
              className="space-y-5"
              noValidate
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    姓名 <span className="text-accent-700">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="box-border h-11 w-full rounded-md border border-background-300/90 bg-background-100/60 px-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    邮箱 <span className="text-accent-700">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="用于接收回复"
                    autoComplete="email"
                    className="box-border h-11 w-full rounded-md border border-background-300/90 bg-background-100/60 px-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                  />
                </div>
              </div>

              <div className="min-w-0">
                <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-medium text-foreground-700">
                  留言主题
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  defaultValue="内容建议"
                  className="box-border h-11 w-full cursor-pointer rounded-md border border-background-300/90 bg-background-100/60 px-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:bg-background-50"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="条目纠错">条目纠错</option>
                  <option value="版权问题">版权问题</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他反馈">其他反馈</option>
                </select>
              </div>

              <div className="min-w-0">
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700">
                    留言内容 <span className="text-accent-700">*</span>
                  </label>
                  <span className="text-[11px] tabular-nums text-foreground-400">
                    {message.length} / {MAX_MESSAGE}
                  </span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  maxLength={MAX_MESSAGE}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="请描述你的建议或问题，涉及具体作品时请附上页面标题"
                  className="box-border w-full resize-none rounded-md border border-background-300/90 bg-background-100/60 px-3 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                ></textarea>
              </div>

              <div className="min-w-0">
                <label htmlFor="contact-referrer-field" className="contact-referrer-note">
                  Website
                </label>
                <input
                  id="contact-referrer-field"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-referrer-note"
                />
              </div>

              {status === "error" && formError ? (
                <div className="rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-xs leading-relaxed text-primary-800">
                  {formError}
                </div>
              ) : null}

              {status === "success" ? (
                <div className="rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
                  <span className="mr-1 font-semibold">提交成功。</span>
                  留言已提交，感谢你的反馈。我们会在工作日尽快查看。
                </div>
              ) : null}

              <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  提交即表示你已阅读并同意本站的内容声明。
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}